import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  CalendarIcon,
  UserIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  PhoneIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import { placementsAPI } from '../../services/api';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { useToast } from '../../context/ToastContext';

interface Placement {
  _id: string;
  clientId: {
    _id: string;
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
  };
  facilityId: {
    _id: string;
    name: string;
    type: string;
    address: {
      street: string;
      city: string;
      state: string;
      zip: string;
    };
    contact: {
      name: string;
      phone: string;
      email: string;
    };
  };
  agentId: {
    _id: string;
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
  };
  status: string;
  priority: string;
  startDate: string;
  endDate?: string;
  moveInDate?: string;
  moveOutDate?: string;
  monthlyRate?: number;
  paymentMethod?: string;
  contractSigned: boolean;
  contractSignedDate?: string;
  depositPaid: boolean;
  depositAmount?: number;
  depositPaidDate?: string;
  insuranceVerified: boolean;
  insuranceVerifiedDate?: string;
  medicalRecordsTransferred: boolean;
  medicalRecordsTransferredDate?: string;
  familyNotified: boolean;
  familyNotifiedDate?: string;
  notes?: string;
  timeline: Array<{
    event: string;
    date: string;
    description?: string;
    _id: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

const PlacementDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success: showSuccess, error: showError } = useToast();

  // Fetch placement details
  const {
    data: placementData,
    isLoading,
    error
  } = useQuery({
    queryKey: ['placement', id],
    queryFn: () => placementsAPI.getPlacement(id!),
    enabled: !!id,
  });

  const placement: Placement = placementData?.data?.data?.placement || null;

  // Delete placement mutation
  const deletePlacementMutation = useMutation({
    mutationFn: (placementId: string) => placementsAPI.deletePlacement(placementId),
    onSuccess: () => {
      showSuccess('Placement deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['placements'] });
      navigate('/placements');
    },
    onError: () => {
      showError('Failed to delete placement');
    },
  });

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this placement? This action cannot be undone.')) {
      deletePlacementMutation.mutate(id!);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'In Progress': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Confirmed': return 'bg-green-100 text-green-800 border-green-200';
      case 'Completed': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'Cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Urgent': return 'bg-red-100 text-red-800 border-red-200';
      case 'High': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCompletionPercentage = () => {
    if (!placement) return 0;
    const checklist = [
      placement.contractSigned,
      placement.depositPaid,
      placement.insuranceVerified,
      placement.medicalRecordsTransferred,
      placement.familyNotified,
      !!placement.moveInDate
    ];
    const completed = checklist.filter(Boolean).length;
    return Math.round((completed / checklist.length) * 100);
  };

  if (isLoading) return <LoadingSpinner />;

  if (error || !placement) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Failed to load placement details.</p>
          <Link to="/placements" className="text-red-600 hover:text-red-800 mt-2 inline-block">
            ← Back to Placements
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <Link
              to="/placements"
              className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back to Placements
            </Link>
            <div className="flex items-center space-x-3">
              <Link
                to={`/placements/${placement._id}/edit`}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <PencilIcon className="w-4 h-4 mr-2" />
                Edit
              </Link>
              <button
                onClick={handleDelete}
                className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <TrashIcon className="w-4 h-4 mr-2" />
                Delete
              </button>
            </div>
          </div>

          {/* Title and Status */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Placement Details
                </h1>
                <p className="text-lg text-gray-600">
                  {placement.clientId.firstName} {placement.clientId.lastName} → {placement.facilityId.name}
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold border ${getStatusColor(placement.status)}`}>
                  {placement.status}
                </span>
                <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold border ${getPriorityColor(placement.priority)}`}>
                  {placement.priority} Priority
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Overview */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Placement Progress</h2>
          <div className="mb-6">
            <div className="flex justify-between items-center mb-3">
              <span className="text-sm font-medium text-gray-700">Overall Progress</span>
              <span className="text-lg font-bold text-gray-900">{getCompletionPercentage()}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-blue-600 h-3 rounded-full transition-all duration-500"
                style={{ width: `${getCompletionPercentage()}%` }}
              ></div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {[
              { label: 'Contract Signed', completed: placement.contractSigned, date: placement.contractSignedDate },
              { label: 'Deposit Paid', completed: placement.depositPaid, date: placement.depositPaidDate },
              { label: 'Insurance Verified', completed: placement.insuranceVerified, date: placement.insuranceVerifiedDate },
              { label: 'Medical Records', completed: placement.medicalRecordsTransferred, date: placement.medicalRecordsTransferredDate },
              { label: 'Family Notified', completed: placement.familyNotified, date: placement.familyNotifiedDate },
              { label: 'Move-in Date', completed: !!placement.moveInDate, date: placement.moveInDate }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className={`w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-3 transition-colors ${
                  item.completed ? 'bg-green-100 border-2 border-green-200' : 'bg-gray-100 border-2 border-gray-200'
                }`}>
                  {item.completed ? (
                    <CheckCircleIcon className="w-7 h-7 text-green-600" />
                  ) : (
                    <ClockIcon className="w-7 h-7 text-gray-400" />
                  )}
                </div>
                <p className="text-sm font-medium text-gray-900 mb-1">{item.label}</p>
                {item.date && (
                  <p className="text-xs text-gray-500">{formatDate(item.date)}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Client Information */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <UserIcon className="w-6 h-6 mr-3 text-blue-600" />
                Client Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Name</label>
                  <p className="text-base text-gray-900">
                    {placement.clientId.firstName} {placement.clientId.lastName}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                  <p className="text-base text-gray-900">{placement.clientId.phone}</p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                  <p className="text-base text-gray-900">{placement.clientId.email}</p>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-gray-200">
                <Link
                  to={`/clients/${placement.clientId._id}`}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium inline-flex items-center"
                >
                  View Client Profile →
                </Link>
              </div>
            </div>

            {/* Facility Information - Updated Layout */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <BuildingOfficeIcon className="w-6 h-6 mr-3 text-blue-600" />
                Facility Information
              </h3>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Facility Name</label>
                    <p className="text-base text-gray-900">{placement.facilityId.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Type</label>
                    <p className="text-base text-gray-900">{placement.facilityId.type}</p>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Address</label>
                  <p className="text-base text-gray-900 leading-relaxed">
                    {placement.facilityId.address?.street}<br />
                    {placement.facilityId.address?.city}, {placement.facilityId.address?.state} {placement.facilityId.address?.zip}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Contact Person</label>
                    <p className="text-base text-gray-900">{placement.facilityId.contact?.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Contact Phone</label>
                    <p className="text-base text-gray-900">{placement.facilityId.contact?.phone}</p>
                  </div>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-gray-200">
                <Link
                  to={`/facilities/${placement.facilityId._id}`}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium inline-flex items-center"
                >
                  View Facility Profile →
                </Link>
              </div>
            </div>

            {/* Timeline */}
            {placement.timeline && placement.timeline.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                  <ClockIcon className="w-6 h-6 mr-3 text-blue-600" />
                  Timeline
                </h3>
                <div className="space-y-6">
                  {placement.timeline.map((event, index) => (
                    <div key={event._id} className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-3 h-3 bg-blue-600 rounded-full mt-2"></div>
                      <div className="min-w-0 flex-1">
                        <p className="text-base font-medium text-gray-900">{event.event}</p>
                        {event.description && (
                          <p className="text-sm text-gray-600 mt-1 leading-relaxed">{event.description}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-2">{formatDate(event.date)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-8">
            {/* Key Details */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Key Details</h3>
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Start Date</label>
                  <p className="text-base text-gray-900">{formatDate(placement.startDate)}</p>
                </div>
                {placement.moveInDate && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Move-in Date</label>
                    <p className="text-base text-green-600 font-medium">{formatDate(placement.moveInDate)}</p>
                  </div>
                )}
                {placement.monthlyRate && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Monthly Rate</label>
                    <p className="text-base text-gray-900 font-medium">{formatCurrency(placement.monthlyRate)}</p>
                  </div>
                )}
                {placement.paymentMethod && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Method</label>
                    <p className="text-base text-gray-900">{placement.paymentMethod}</p>
                  </div>
                )}
                {placement.depositAmount && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Deposit Amount</label>
                    <p className="text-base text-gray-900 font-medium">{formatCurrency(placement.depositAmount)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Assigned Agent */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Assigned Agent</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-lg font-medium text-gray-900">
                    {placement.agentId.firstName} {placement.agentId.lastName}
                  </p>
                </div>
                <div className="space-y-3">
                  <a
                    href={`tel:${placement.agentId.phone}`}
                    className="flex items-center text-sm text-gray-600 hover:text-blue-600 transition-colors"
                  >
                    <PhoneIcon className="w-4 h-4 mr-3" />
                    {placement.agentId.phone}
                  </a>
                  <a
                    href={`mailto:${placement.agentId.email}`}
                    className="flex items-center text-sm text-gray-600 hover:text-blue-600 transition-colors"
                  >
                    <EnvelopeIcon className="w-4 h-4 mr-3" />
                    {placement.agentId.email}
                  </a>
                </div>
              </div>
            </div>

            {/* Notes */}
            {placement.notes && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                  <DocumentTextIcon className="w-6 h-6 mr-3 text-blue-600" />
                  Notes
                </h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{placement.notes}</p>
                </div>
              </div>
            )}

            {/* Metadata */}
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Metadata</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700">Created:</span>
                  <span className="text-gray-600">{formatDate(placement.createdAt)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700">Updated:</span>
                  <span className="text-gray-600">{formatDate(placement.updatedAt)}</span>
                </div>
                <div className="pt-2 border-t border-gray-200">
                  <span className="font-medium text-gray-700 text-xs">ID:</span>
                  <p className="text-xs text-gray-500 mt-1 font-mono break-all">{placement._id}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlacementDetail;
